import { dgsSignBase64 } from "@/assets/dgsSign";

const CERTIFICATE_IMAGES = "uploads/general/images/App%20Level%20Images";

export type CertificationData = {
  issueDate: string;
  certificationId: string;
  assessment: {
    course: {
      name: string;
      topics: { name: string }[];
    };
  };
  user: {
    profileImage?: string;
    name: string;
    indianCdc?: string;
    rank?: string;
    rankDesignation?: string;
  };
};

export function openCertificateTab(courseId: string) {
  // Kept for direct-link / fallback use. Primary UI uses CertificateModal.
  window.open(
    `/dashboard/certificate/${courseId}`,
    "_blank",
    "noopener,noreferrer",
  );
}

export function CertificateContent({ data }: { data: CertificationData }) {
  const { issueDate, certificationId, assessment, user } = data;
  const { course } = assessment;
  const { profileImage, name, indianCdc } = user;
  const rank = user.rankDesignation || user.rank;
  const cdnLink = import.meta.env.VITE_CDN_LINK;

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        .certificate-page {
          margin: 0;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: auto;
          width: 100%;
          background: #fff;
          font-family: Arial, sans-serif;
        }
        @media print {
          .certificate-page {
            min-height: 100vh;
          }
        }
        .certificate-container {
          width: 100%;
          max-width: 210mm;
          height: auto;
          padding: 12mm 20mm;
          box-sizing: border-box;
          border: 2px solid #000;
        }
        .certificate-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .certificate-header img {
          height: 70px;
          object-fit: contain;
        }
        .certificate-mti-name {
          font-family: "Times New Roman", serif;
          font-size: 28px;
          font-weight: bold;
          text-align: center;
        }
        .certificate-section {
          margin: 12px 0;
          font-size: 16px;
          line-height: 1.6;
        }
        .certificate-bold {
          font-weight: bold;
        }
        .certificate-topics {
          margin: 15px 0;
          font-size: 14px;
          line-height: 1.5;
          text-align: left;
        }
        .certificate-topic-item {
          margin: 3px 0;
          font-size: 12px;
        }
        .certificate-profile-block {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-top: 25px;
        }
        .certificate-profile-pic {
          width: 110px;
          height: 130px;
          border: 1px solid #000;
          object-fit: cover;
        }
        .certificate-issue-column {
          display: flex;
          flex-direction: column;
          width: 45%;
        }
        .certificate-issue-details {
          font-size: 14px;
          line-height: 1.6;
          text-align: left;
        }
        .certificate-signature-block {
          text-align: center;
          margin-top: 24px;
          font-size: 14px;
        }
        .certificate-signature-block img {
          height: 80px;
          object-fit: contain;
          display: block;
          margin: 0 auto 2px;
        }
        .certificate-signature-name {
          font-weight: bold;
        }
      `}</style>

      <div className="certificate-page">
        <div className="certificate-container">
          <div className="certificate-header">
            <div style={{ display: "flex", gap: "15px" }}>
              <img
                src={`${cdnLink}${CERTIFICATE_IMAGES}/nusi.svg`}
                alt="NUSI Logo"
              />
              <img
                src="https://data.sagarmeinyog.com//DGMA_logo.png"
                alt="DG Shipping Logo"
              />
            </div>
            <div className="certificate-mti-name">Sagar Mein Yog</div>
            <div style={{ display: "flex", gap: "15px" }}>
              <img
                src={`${cdnLink}${CERTIFICATE_IMAGES}/logo.svg`}
                alt="SMY Logo"
              />
              <img
                src={`${cdnLink}${CERTIFICATE_IMAGES}/Trijog.svg`}
                alt="Trijog Logo"
              />
            </div>
          </div>

          <div className="certificate-section" style={{ textAlign: "right" }}>
            Certificate No: {certificationId}
          </div>

          <div className="certificate-section" style={{ textAlign: "left" }}>
            This is to certify that{" "}
            <span className="certificate-bold">{name}</span>
            <br />
            <strong>INDoS No:</strong> {indianCdc || ""}
            <br />
          </div>

          <div className="certificate-section" style={{ textAlign: "center" }}>
            has successfully completed Course
            <br />
            <span className="certificate-bold">{course.name}</span>
            {rank ? (
              <>
                <br />
                of not less than 6 months while sailing as {rank}
              </>
            ) : null}
          </div>

          <div className="certificate-topics">
            <div className="certificate-bold" style={{ marginBottom: "6px" }}>
              Covering the topics:
            </div>
            {course.topics?.map((topic) => (
              <div key={topic.name} className="certificate-topic-item">
                {topic.name}
              </div>
            ))}
          </div>

          <div className="certificate-section" style={{ textAlign: "center" }}>
            The above Training Program is approved by <br />
            <span className="certificate-bold">
              The Directorate General of Maritime Administration, Government of India
            </span>
          </div>

          <div className="certificate-profile-block">
            {profileImage ? (
              <div style={{ textAlign: "center" }}>
                <strong>Candidate Photo</strong>
                <br />
                <img
                  className="certificate-profile-pic"
                  src={`${cdnLink}${profileImage}`}
                  alt="Candidate Photo"
                  style={{
                    marginTop: "8px",
                    borderRadius: "8px",
                    maxWidth: "120px",
                  }}
                />
              </div>
            ) : null}

            <div className="certificate-issue-column">
              <div className="certificate-issue-details">
                Issued on: {new Date(issueDate).toLocaleDateString()}
                <br />
                Date of Expiry: UNLIMITED
              </div>
              <div className="certificate-signature-block">
                <img src={dgsSignBase64} alt="DGS Signature" />
                <div className="certificate-signature-name">
                  (Deependra Singh Bisen)
                </div>
                <div>
                  Dy. Director of General of Maritime Administration (Trg.)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
